<script lang="ts">
import { page } from '$app/state'
import {
  STUDY_CHART_JS_GATE,
  type StudyChartJsGate,
} from '$lib/components/study/study-chart-js-gate'
import { baseChartOptions, getStudyChartThemeColors } from '$lib/utils/report/chart-config'
import { getContext } from 'svelte'

export interface LineDataset {
  data: (number | null)[]
  borderColor: string
  backgroundColor: string
  label?: string
  borderWidth?: number
  pointHoverRadius?: number
  borderDash?: number[]
  /** Chart.js fill target. Default `true` (fill to origin). Use `'-1'` to fill to the previous
   *  dataset (e.g. to shade a band between two boundary series), or `false` for a plain line. */
  fill?: number | string | boolean
}

export interface LineEventMarker {
  /** Must match exactly one of the chart's x-axis labels. */
  monthLabel: string
  color: string
}

type ThemeColors = ReturnType<typeof getStudyChartThemeColors>

type HoverState = {
  x: number
  value: number
  label: string
  index: number
  datasetIndex: number
}

/** Chart.js instance shape used by the crosshair plugin (avoid importing chart types). */
type ChartLike = {
  getElementsAtEventForMode: (
    e: unknown,
    mode: string,
    opts: { intersect: boolean },
    useFinalPosition: boolean,
  ) => { index: number; datasetIndex: number }[]
  getDatasetMeta: (i: number) => { data: { x: number; y: number }[] }
  data: { labels?: unknown[]; datasets: { data: unknown[]; label?: string }[] }
  chartArea: { top: number; bottom: number; left: number; right: number }
  ctx: CanvasRenderingContext2D
  draw: () => void
}

function createCrosshairPlugin(
  th: ThemeColors,
  tooltipFormat: (value: number, datasetLabel: string) => string,
) {
  const chartHover = new WeakMap<object, HoverState | null>()

  return {
    id: 'studyLineCrosshair',
    afterEvent(chart: ChartLike, args: { event: { type: string }; inChartArea?: boolean }) {
      const evt = args.event
      if (evt.type === 'mouseout' || evt.type === 'pointerleave') {
        chartHover.set(chart, null)
        chart.draw()
        return
      }
      if (evt.type !== 'mousemove') return

      const hitEvent =
        typeof evt === 'object' && evt !== null && 'native' in evt
          ? (evt as { native: Event }).native
          : (evt as Event)
      let items = chart.getElementsAtEventForMode(hitEvent, 'index', { intersect: false }, false)
      if (!items.length && hitEvent !== evt) {
        items = chart.getElementsAtEventForMode(evt as Event, 'index', { intersect: false }, false)
      }
      if (!items.length) {
        if (chartHover.get(chart) != null) {
          chartHover.set(chart, null)
          chart.draw()
        }
        return
      }

      const { index, datasetIndex } = items[0]
      const meta = chart.getDatasetMeta(datasetIndex)
      const pt = meta.data[index]
      if (!pt) return

      const value = Number(chart.data.datasets[datasetIndex].data[index])
      const label = String(chart.data.labels?.[index] ?? '')
      const next: HoverState = { x: pt.x, value, label, index, datasetIndex }
      const prev = chartHover.get(chart)
      if (
        prev &&
        prev.index === next.index &&
        prev.datasetIndex === next.datasetIndex &&
        Math.abs(prev.x - next.x) < 0.5 &&
        prev.value === next.value
      ) {
        return
      }
      chartHover.set(chart, next)
      chart.draw()
    },
    afterDraw(chart: ChartLike) {
      const h = chartHover.get(chart)
      if (!h) return
      const { chartArea, ctx } = chart
      const pad = 10

      ctx.save()
      ctx.beginPath()
      ctx.strokeStyle = th.crosshair
      ctx.lineWidth = 1
      ctx.moveTo(h.x, chartArea.top)
      ctx.lineTo(h.x, chartArea.bottom)
      ctx.stroke()

      ctx.textAlign = 'right'
      ctx.textBaseline = 'top'
      let y = chartArea.top + pad
      if (h.label) {
        ctx.font = '500 10px ui-sans-serif, system-ui, sans-serif'
        ctx.fillStyle = th.legend
        ctx.fillText(h.label, chartArea.right - pad, y)
        y += 14
      }
      ctx.font = '600 12px ui-sans-serif, system-ui, sans-serif'
      ctx.fillStyle = th.tick
      const datasetLabel = String(chart.data.datasets[h.datasetIndex]?.label ?? '')
      const valueLine = tooltipFormat(h.value, datasetLabel).trim()
      ctx.fillText(valueLine, chartArea.right - pad, y)
      ctx.restore()
    },
  }
}

interface Props {
  labels: string[]
  datasets: LineDataset[]
  yTickFormat?: (v: number) => string
  tooltipFormat?: (value: number, datasetLabel: string) => string
  maxTicksLimit?: number
  showLegend?: boolean
  /** Render legend text at full strength instead of the muted default. For charts where
   *  the legend is load-bearing — several series the reader has to tell apart. */
  strongLegend?: boolean
  active?: boolean
  height?: string
  /** Vertical crosshair, value readout top-right inside plot area; hides default tooltip. */
  crosshairOverlay?: boolean
  /** Vertical event marker lines drawn at specific x-axis label positions. */
  eventMarkers?: LineEventMarker[]
}

let {
  labels,
  datasets,
  yTickFormat = (v) => String(v),
  tooltipFormat = (v) => String(v),
  maxTicksLimit,
  showLegend = false,
  strongLegend = false,
  active = false,
  height = '260px',
  crosshairOverlay = false,
  eventMarkers = [],
}: Props = $props()

let canvas: HTMLCanvasElement
let built = $state(false)
let chart: { destroy(): void } | null = null

const chartJsGate = getContext<StudyChartJsGate | undefined>(STUDY_CHART_JS_GATE)

$effect(() => {
  page.url.search
  void chartJsGate?.ready
  if (!active || !canvas) return
  const Chart = (window as Window & { Chart?: unknown }).Chart as
    | { new (el: HTMLCanvasElement, cfg: object): { destroy(): void } }
    | undefined
  if (!Chart) return

  if (chart) {
    chart.destroy()
    chart = null
  }
  built = true

  const host = (canvas.closest('.study-presentation') ?? canvas) as HTMLElement
  const th = getStudyChartThemeColors(host)
  const crosshairPlugin = crosshairOverlay ? createCrosshairPlugin(th, tooltipFormat) : null

  const markers = eventMarkers
  const eventMarkerPlugin =
    markers.length > 0
      ? {
          id: 'eventMarkerLines',
          afterDraw(c: ChartLike) {
            const { ctx, chartArea } = c
            const chartLabels = c.data.labels as string[]
            const meta0 = c.getDatasetMeta(0)
            ctx.save()
            for (const marker of markers) {
              const idx = chartLabels.indexOf(marker.monthLabel)
              if (idx < 0) continue
              const pt = meta0.data[idx]
              if (!pt) continue
              ctx.beginPath()
              ctx.strokeStyle = marker.color
              ctx.lineWidth = 1.5
              ctx.setLineDash([4, 3])
              ctx.moveTo(pt.x, chartArea.top)
              ctx.lineTo(pt.x, chartArea.bottom)
              ctx.stroke()
              ctx.setLineDash([])
              ctx.beginPath()
              ctx.fillStyle = marker.color
              ctx.arc(pt.x, chartArea.top + 4, 3, 0, Math.PI * 2)
              ctx.fill()
            }
            ctx.restore()
          },
        }
      : null

  chart = new Chart(canvas, {
    type: 'line',
    plugins: [crosshairPlugin, eventMarkerPlugin].filter(Boolean) as object[],
    data: {
      labels,
      datasets: datasets.map((ds) => ({
        data: ds.data,
        borderColor: ds.borderColor,
        backgroundColor: ds.backgroundColor,
        label: ds.label ?? '',
        borderWidth: ds.borderWidth ?? 2,
        fill: ds.fill ?? true,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: ds.pointHoverRadius ?? 0,
        ...(ds.borderDash ? { borderDash: ds.borderDash } : {}),
      })),
    },
    options: {
      ...baseChartOptions,
      // intersect:false lets the tooltip light up anywhere along the x-axis,
      // not just when the cursor is exactly on a data point. Crosshair-overlay
      // mode (below) layers a different visual on top but uses the same hover.
      interaction: {
        mode: 'index' as const,
        intersect: false,
        axis: 'x' as const,
      },
      plugins: {
        ...baseChartOptions.plugins,
        legend: showLegend
          ? {
              display: true,
              labels: {
                color: strongLegend ? th.legendStrong : th.legend,
                boxWidth: 9,
                boxHeight: 9,
                font: { size: 9 },
                padding: 8,
                /**
                 * Solid squares. Chart.js builds a line-series legend swatch from the dataset's
                 * `backgroundColor`, which is `'transparent'` on every study line chart (the fill
                 * is off), so the default swatch renders as an empty outline. Generate the items
                 * from the datasets instead and fill from `borderColor` — the line's own colour.
                 * `isDatasetVisible` is preserved so click-to-toggle still greys the entry out.
                 *
                 * `fontColor` is REQUIRED here, not cosmetic: Chart.js's legend draws each item's
                 * text from `legendItem.fontColor`, not from `options.labels.color` directly — that
                 * option only reaches the text because the *default* generateLabels copies it onto
                 * `fontColor` for us. Overriding generateLabels without also setting `fontColor`
                 * leaves it `undefined`, and `ctx.fillStyle = undefined` is a silent no-op, so the
                 * text renders in whatever colour the canvas last had (black, here).
                 */
                generateLabels: (chart: {
                  data: { datasets: LineDataset[] }
                  isDatasetVisible: (i: number) => boolean
                }) =>
                  chart.data.datasets.map((dataset, index) => ({
                    text: dataset.label ?? '',
                    fillStyle: dataset.borderColor,
                    strokeStyle: dataset.borderColor,
                    fontColor: strongLegend ? th.legendStrong : th.legend,
                    lineWidth: 0,
                    pointStyle: 'rect' as const,
                    hidden: !chart.isDatasetVisible(index),
                    datasetIndex: index,
                  })),
              },
            }
          : { display: false },
        tooltip: crosshairOverlay
          ? { enabled: false }
          : {
              ...baseChartOptions.plugins.tooltip,
              backgroundColor: th.tooltipBg,
              borderColor: th.tooltipBorder,
              titleColor: th.tooltipTitle,
              bodyColor: th.tooltipBody,
              // Collapse the actuals→projection handoff: when two datasets share a value at
              // the same x-index AND one is solid + one is dashed (the canonical "connector
              // point" pattern), keep only the first. Coincidentally-equal values across two
              // legit overlapping series (both solid or both dashed) are preserved.
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              filter: (item: any, idx: number, items: any[]) => {
                const dupIdx = items.findIndex(
                  (i) => i.dataIndex === item.dataIndex && i.parsed.y === item.parsed.y,
                )
                if (dupIdx === idx) return true
                const earlierDashed = !!items[dupIdx]?.dataset?.borderDash
                const currentDashed = !!item?.dataset?.borderDash
                // Drop only when one series is solid and the other dashed (handoff pair).
                return earlierDashed === currentDashed
              },
              callbacks: {
                label: (c: { dataset: { label: string }; raw: number }) =>
                  tooltipFormat(c.raw, c.dataset.label ?? ''),
              },
            },
      },
      scales: {
        x: {
          ...baseChartOptions.scales.x,
          grid: { color: th.grid },
          ticks: maxTicksLimit
            ? { ...baseChartOptions.scales.x.ticks, color: th.tick, maxTicksLimit }
            : { ...baseChartOptions.scales.x.ticks, color: th.tick },
        },
        y: {
          ...baseChartOptions.scales.y,
          grid: { color: th.grid },
          ticks: { ...baseChartOptions.scales.y.ticks, color: th.tick, callback: yTickFormat },
        },
      },
    },
  })

  return () => {
    if (chart) {
      chart.destroy()
      chart = null
    }
    built = false
  }
})
</script>

<div class="ch" style="height:{height}">
  <canvas bind:this={canvas}></canvas>
</div>
